import * as d3 from 'd3';
import { Block, BlockGeometry, EditMode, Position } from '../../types';
import { setupTextEditing } from '../../utils/textEditor';
import { getBlockDimensions, getBlockFill } from '../../utils/blockUtils';
import { implementDrag } from '../utils/dragUtils';
import { implementResize } from '../utils/resizeUtils';
import { createBpmnBlockBackground } from '../../utils/bpmnShapeRenderer';
import { createNotationBlockBackground } from '../../utils/notationShapeRenderer';
import { isBpmnSymbolId } from '../../../data/bpmnSymbols';
import { isDmnSymbolId } from '../../../data/dmnSymbols';
import { DMN_ICON_MARKUP } from '../../../data/dmnIconMarkup';
import { isVsmSymbolId } from '../../../data/vsmSymbols';
import { VSM_ICON_MARKUP } from '../../../data/vsmIconMarkup';
import { appendBpmnMeaningCaption } from '../../utils/bpmnMeaningCaption';

function hasNotationGlyph(note: Block): boolean {
  return Boolean(note.bpmnSymbol || note.dmnSymbol || note.vsmSymbol);
}

function appendTextAnnotationLayout(
  block: d3.Selection<HTMLDivElement, unknown, null, undefined>,
  note: Block,
  dimensions: { width: number; height: number }
): HTMLElement | null {
  block
    .style('flex-direction', 'row')
    .style('align-items', 'stretch')
    .style('justify-content', 'flex-start')
    .style('gap', '6px')
    .style('background', 'transparent')
    .style('border', 'none')
    .style('box-shadow', 'none')
    .style('overflow', 'visible');

  const bracket = block
    .append('svg')
    .attr('width', 12)
    .attr('height', dimensions.height)
    .attr('viewBox', `0 0 12 ${dimensions.height}`)
    .style('flex', '0 0 12px')
    .style('display', 'block');

  const inset = 1.5;
  bracket
    .append('path')
    .attr(
      'd',
      `M ${12 - inset} ${inset} L ${inset} ${inset} L ${inset} ${dimensions.height - inset} L ${12 - inset} ${dimensions.height - inset}`
    )
    .attr('fill', 'none')
    .attr('stroke', '#1A202C')
    .attr('stroke-width', 2)
    .attr('stroke-linecap', 'square')
    .attr('stroke-linejoin', 'miter');

  return block
    .append('div')
    .attr('class', 'text-annotation-content')
    .text(note.text || '')
    .style('box-sizing', 'border-box')
    .style('flex', '1 1 auto')
    .style('min-width', '0')
    .style('min-height', '0')
    .style('height', '100%')
    .style('padding', note.textStyle?.padding != null ? `${note.textStyle.padding}px` : '4px 6px')
    .style('text-align', note.textStyle?.textAlign || 'left')
    .style('font-size', note.textStyle?.fontSize ? `${note.textStyle.fontSize}px` : '13px')
    .style('font-weight', note.textStyle?.fontWeight || 'normal')
    .style('font-family', note.textStyle?.fontFamily || 'inherit')
    .style('font-style', note.textStyle?.fontStyle || 'normal')
    .style('color', note.textStyle?.textColor || '#111827')
    .style('line-height', note.textStyle?.lineHeight || '1.3')
    .style('text-transform', note.textStyle?.textTransform || 'none')
    .style('text-decoration', note.textStyle?.textDecoration || 'none')
    .style('white-space', 'pre-wrap')
    .style('overflow-wrap', 'anywhere')
    .style('word-break', 'break-word')
    .style('overflow', 'hidden')
    .style('cursor', 'move')
    .style('user-select', 'none')
    .style('-webkit-user-select', 'none')
    .on('mousedown', (event: MouseEvent) => {
      // Keep drag working when grabbing the text area; edit only on double-click.
      if (event.detail > 1) return;
      event.preventDefault();
    })
    .node();
}

export function createNoteBlock(
  container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
  note: Block,
  editMode: EditMode,
  onDragEnd: (id: string, position: BlockGeometry) => void,
  onTextUpdate: (id: string, newText: string) => void,
  allBlocks: Block[] = [],
  t: (key: string) => string,
  setBlocks: React.Dispatch<React.SetStateAction<Block[]>>,
  onDragMove?: (updates: Array<{ id: string; position: Position }>) => void,
  getSelectedBlockIds?: () => string[],
  onResizeMove?: (id: string, geometry: BlockGeometry) => void
) {
  const dimensions = getBlockDimensions(note);
  const fill = getBlockFill(note);
  const isTextAnnotation =
    note.bpmnSymbol === 'textAnnotation' || note.dmnSymbol === 'textAnnotation';

  const blockContainer = container
    .append('div')
    .attr('class', `note-block-container-${note.id} block`)
    .style('position', 'absolute')
    .style('left', `${note.x || 0}px`)
    .style('top', `${note.y || 0}px`)
    .style('z-index', '2');

  const block = blockContainer
    .append('div')
    .attr('id', note.id)
    .attr('class', `note-block-${note.id} note-block`)
    .style('width', `${dimensions.width}px`)
    .style('height', `${dimensions.height}px`)
    .style('position', 'relative')
    .style('cursor', editMode.enabled ? 'move' : 'default')
    .style('user-select', 'none')
    .style('background', hasNotationGlyph(note) ? 'transparent' : fill)
    .style('border', hasNotationGlyph(note) ? 'none' : '1px solid rgba(0,0,0,0.2)')
    .style('border-radius', hasNotationGlyph(note) ? '0' : '4px')
    .style('box-shadow', hasNotationGlyph(note) ? 'none' : '0 2px 6px rgba(0,0,0,0.15)')
    .style('display', 'flex')
    .style('flex-direction', 'column')
    .style('align-items', 'center')
    .style('justify-content', 'center')
    .style('overflow', 'hidden');

  let textElement: HTMLElement | null = null;

  if (isTextAnnotation) {
    textElement = appendTextAnnotationLayout(block, note, dimensions);
  } else if (note.bpmnSymbol && isBpmnSymbolId(note.bpmnSymbol)) {
    createBpmnBlockBackground(
      block,
      note.bpmnSymbol,
      dimensions.width,
      dimensions.height,
      '#ffffff',
      '#1A202C'
    );
    appendBpmnMeaningCaption(
      blockContainer,
      note,
      dimensions.height,
      dimensions.width,
      editMode,
      onTextUpdate,
    );
  } else if (note.dmnSymbol && isDmnSymbolId(note.dmnSymbol)) {
    const icon = DMN_ICON_MARKUP[note.dmnSymbol];
    if (icon) {
      createNotationBlockBackground(block, icon.viewBox, icon.markup, dimensions.width, dimensions.height, `dmn-${note.id}`);
    }
  } else if (note.vsmSymbol && isVsmSymbolId(note.vsmSymbol)) {
    const icon = VSM_ICON_MARKUP[note.vsmSymbol];
    if (icon) {
      createNotationBlockBackground(block, icon.viewBox, icon.markup, dimensions.width, dimensions.height, `vsm-${note.id}`);
    }
  } else {
    textElement = block
      .append('div')
      .text(note.text || '')
      .style('box-sizing', 'border-box')
      .style(
        'padding',
        note.textStyle?.padding != null ? `${note.textStyle.padding}px` : '4px 6px'
      )
      .style('text-align', note.textStyle?.textAlign || 'center')
      .style('font-size', note.textStyle?.fontSize ? `${note.textStyle.fontSize}px` : '14px')
      .style('font-weight', note.textStyle?.fontWeight || 'normal')
      .style('font-family', note.textStyle?.fontFamily || 'inherit')
      .style('font-style', note.textStyle?.fontStyle || 'normal')
      .style('color', note.textStyle?.textColor || '#111827')
      .style('position', 'relative')
      .style('z-index', '1')
      .style('line-height', note.textStyle?.lineHeight || '1.25')
      .style('text-transform', note.textStyle?.textTransform || 'none')
      .style('text-decoration', note.textStyle?.textDecoration || 'none')
      .style('width', '100%')
      .style('max-width', '100%')
      .style('max-height', '100%')
      .style('min-height', '0')
      .style('flex', '0 1 auto')
      .style('white-space', 'pre-wrap')
      .style('overflow-wrap', 'break-word')
      .style('word-break', 'break-word')
      .style('overflow-y', 'auto')
      .style('overflow-x', 'hidden')
      .node();
  }

  // Badges are rendered and kept in sync by ProcessManager.updateNoteBadge().

  if (textElement) {
    setupTextEditing(textElement, note, editMode.enabled, onTextUpdate);
  }

  if (editMode.enabled) {
    implementDrag(blockContainer, note.id, allBlocks, onDragEnd, setBlocks, onDragMove, getSelectedBlockIds);
    implementResize(blockContainer, note, onDragEnd, setBlocks, onResizeMove);
  }

  return blockContainer;
}
